import type { CoachVeeraBehavioralReadModel } from "@/lib/behavior/coachVeeraBehavioralReadModel";
import { normalizeBehavioralKey } from "@/lib/behavior/substrate";

export type CoachLeaderLongitudinalCoverage = Readonly<{
  aggregateSource: "bi_v2" | "compatibility_only_v1" | "none";
  historicalMeetingCount: number | null;
  sourceVerifiedMeetingCount: number;
  sourceVerifiedArtifactCount: number;
  legacyEvidencePresent: boolean | null;
  legacyObservationWindowTruncated: boolean | null;
  graphEvidenceCount: number | null;
  authorizedSourceReadiness: "ready" | "partial" | "unavailable";
  exactSourceCoverage: "none" | "some" | "threshold_met";
}>;

export type CoachLeaderLongitudinalSourceType = "transcript_upload" | "post_meeting_reflection";

export type CoachLeaderAggregateEvidenceRef = Readonly<{
  behaviorKey: string;
  sourceType: CoachLeaderLongitudinalSourceType;
  sourceArtifactId: string;
  meetingId: string | null;
  evidenceType: "reflection" | "transcript" | "derived";
  isContradiction: boolean;
}>;

export type CoachLeaderAuthorizedSourceRef = Readonly<{
  sourceType: string;
  sourceId: string;
  meetingId: string | null;
  title: string;
  occurredAt: string | null;
  route: string;
}>;

export type CoachLeaderLongitudinalEpisode = Readonly<{
  meetingId: string | null;
  title: string;
  occurredAt: string | null;
  polarity: "support" | "contextual_challenge";
  source: Readonly<{
    route: string;
    identity: Readonly<{ sourceType: string; sourceId: string }>;
  }>;
}>;

export type CoachLeaderLongitudinalRead =
  | Readonly<{ state: "loading" }>
  | Readonly<{ state: "revoked" }>
  | Readonly<{ state: "unavailable"; retryable: boolean }>
  | Readonly<{ state: "insufficient_history"; coverage: CoachLeaderLongitudinalCoverage }>
  | Readonly<{
      state: "historical_signal" | "emerging_finding" | "evidence_backed_finding";
      finding: Readonly<{
        behaviorKey: string;
        label: string;
        headline: string;
        explanation: string;
        whyNow: string;
        question: string;
      }>;
      confidence: "low" | "medium" | "high";
      coverage: CoachLeaderLongitudinalCoverage;
      supportingEpisodes: readonly CoachLeaderLongitudinalEpisode[];
      challengingEpisodes: readonly CoachLeaderLongitudinalEpisode[];
      contradiction: Readonly<{
        present: boolean;
        count: number;
        context: "same_theme" | "cross_context" | "unknown";
      }>;
    }>;

export type BuildCoachLeaderLongitudinalReadInput =
  | Readonly<{ status: "loading" | "revoked" }>
  | Readonly<{ status: "unavailable"; retryable: boolean }>
  | Readonly<{
      status: "ready";
      aggregate: CoachVeeraBehavioralReadModel;
      aggregateEvidenceRefs: readonly CoachLeaderAggregateEvidenceRef[];
      authorizedSources:
        | Readonly<{ status: "ready" | "partial"; sources: readonly CoachLeaderAuthorizedSourceRef[] }>
        | Readonly<{ status: "operational_error"; sources: readonly [] }>;
      legacyEvidencePresent: boolean | null;
      observationWindowTruncated: boolean | null;
    }>;

type MatchedEpisode = Readonly<{
  aggregate: CoachLeaderAggregateEvidenceRef;
  meetingId: string | null;
  source: CoachLeaderAuthorizedSourceRef;
  sourceType: CoachLeaderLongitudinalSourceType;
}>;

const SOURCE_TYPE_ALIASES: Readonly<Record<string, CoachLeaderLongitudinalSourceType>> = Object.freeze({
  transcript: "transcript_upload",
  transcript_upload: "transcript_upload",
  reflection: "post_meeting_reflection",
  post_meeting_reflection: "post_meeting_reflection",
});

const lexicalCompare = (left: string, right: string) => left.localeCompare(right);

export function canonicalizeCoachLeaderLongitudinalSourceType(
  value: string,
): CoachLeaderLongitudinalSourceType | null {
  return SOURCE_TYPE_ALIASES[value.trim().toLowerCase()] ?? null;
}

function sourceKey(sourceType: CoachLeaderLongitudinalSourceType, sourceId: string): string {
  return `${sourceType}\u0000${sourceId}`;
}

function aggregateSourceOf(model: CoachVeeraBehavioralReadModel): CoachLeaderLongitudinalCoverage["aggregateSource"] {
  switch (model.primaryObservation.source) {
    case "bi_v2": return "bi_v2";
    case "compatibility_only_v1": return "compatibility_only_v1";
    case "no_behavioral_data": return "none";
  }
}

function historicalMeetingCount(model: CoachVeeraBehavioralReadModel): number | null {
  if (model.supportSummary.kind === "canonical") return model.supportSummary.meetingCount;
  if (model.supportSummary.kind === "compatibility_only_v1") return model.supportSummary.legacyMeetingCount;
  return null;
}

function episodeCompare(left: MatchedEpisode, right: MatchedEpisode): number {
  const leftTime = left.source.occurredAt ?? "";
  const rightTime = right.source.occurredAt ?? "";
  const time = lexicalCompare(rightTime, leftTime);
  if (time !== 0) return time;
  const type = lexicalCompare(left.sourceType, right.sourceType);
  if (type !== 0) return type;
  const source = lexicalCompare(left.source.sourceId, right.source.sourceId);
  if (source !== 0) return source;
  return Number(left.aggregate.isContradiction) - Number(right.aggregate.isContradiction);
}

function intersectAuthorizedAggregateSources(
  aggregateRefs: readonly CoachLeaderAggregateEvidenceRef[],
  authorizedSources: readonly CoachLeaderAuthorizedSourceRef[],
  behaviorKey: string,
): readonly MatchedEpisode[] {
  const targetBehaviorKey = normalizeBehavioralKey(behaviorKey);
  const sourceMap = new Map<string, CoachLeaderAuthorizedSourceRef[]>();
  for (const source of authorizedSources) {
    const canonicalType = canonicalizeCoachLeaderLongitudinalSourceType(source.sourceType);
    if (!canonicalType) continue;
    const key = sourceKey(canonicalType, source.sourceId);
    sourceMap.set(key, [...(sourceMap.get(key) ?? []), source]);
  }

  const aggregateMap = new Map<string, Array<{
    ref: CoachLeaderAggregateEvidenceRef;
    sourceType: CoachLeaderLongitudinalSourceType;
  }>>();
  for (const ref of aggregateRefs) {
    const sourceType = canonicalizeCoachLeaderLongitudinalSourceType(ref.sourceType);
    if (!sourceType || normalizeBehavioralKey(ref.behaviorKey) !== targetBehaviorKey) continue;
    const key = sourceKey(sourceType, ref.sourceArtifactId);
    aggregateMap.set(key, [...(aggregateMap.get(key) ?? []), { ref, sourceType }]);
  }

  const matches: MatchedEpisode[] = [];
  for (const [key, refs] of aggregateMap) {
    const sources = sourceMap.get(key) ?? [];
    if (sources.length !== 1 || refs.length !== 1) continue;
    const [{ ref, sourceType }] = refs;
    const [source] = sources;
    if (ref.meetingId !== null && source.meetingId !== null && ref.meetingId !== source.meetingId) continue;
    matches.push({
      aggregate: ref,
      meetingId: ref.meetingId ?? source.meetingId,
      source,
      sourceType,
    });
  }

  return matches.sort(episodeCompare);
}

function independentMeetingCount(matches: readonly MatchedEpisode[]): number {
  return new Set(matches.flatMap((match) => match.meetingId === null ? [] : [match.meetingId])).size;
}

function toEpisode(match: MatchedEpisode): CoachLeaderLongitudinalEpisode {
  return {
    meetingId: match.meetingId,
    title: match.source.title,
    occurredAt: match.source.occurredAt,
    polarity: match.aggregate.isContradiction ? "contextual_challenge" : "support",
    source: {
      route: match.source.route,
      identity: { sourceType: match.sourceType, sourceId: match.source.sourceId },
    },
  };
}

function dedupeByMeeting(matches: readonly MatchedEpisode[]): readonly MatchedEpisode[] {
  const seenMeetings = new Set<string>();
  return matches.filter((match) => {
    const meetingId = match.meetingId;
    if (meetingId === null) return true;
    if (seenMeetings.has(meetingId)) return false;
    seenMeetings.add(meetingId);
    return true;
  });
}

function buildCoverage(
  aggregate: CoachVeeraBehavioralReadModel,
  matches: readonly MatchedEpisode[],
  authorizedSourceReadiness: CoachLeaderLongitudinalCoverage["authorizedSourceReadiness"],
  legacyEvidencePresent: boolean | null,
  observationWindowTruncated: boolean | null,
): CoachLeaderLongitudinalCoverage {
  const meetingCount = independentMeetingCount(matches);
  const exactSourceCoverage = meetingCount >= 2
    ? "threshold_met"
    : matches.length > 0
      ? "some"
      : "none";
  return {
    aggregateSource: aggregateSourceOf(aggregate),
    historicalMeetingCount: historicalMeetingCount(aggregate),
    sourceVerifiedMeetingCount: meetingCount,
    sourceVerifiedArtifactCount: matches.length,
    legacyEvidencePresent,
    legacyObservationWindowTruncated: observationWindowTruncated,
    graphEvidenceCount: aggregate.sourceSummary.graphEvidenceCount,
    authorizedSourceReadiness,
    exactSourceCoverage,
  };
}

function contradictionFor(
  aggregate: CoachVeeraBehavioralReadModel,
  matches: readonly MatchedEpisode[],
): { present: boolean; count: number; context: "same_theme" | "cross_context" | "unknown" } {
  const behaviorKey = normalizeBehavioralKey(aggregate.primaryObservation.behaviorKey);
  const aggregateCount = new Set(aggregate.contradictions
    .filter((contradiction) => normalizeBehavioralKey(contradiction.behaviorKey) === behaviorKey)
    .map((contradiction, index) => contradiction.evidenceRefId ?? `aggregate:${index}`)).size;
  const observationCount = matches.filter((match) => match.aggregate.isContradiction).length;
  // Aggregate graph and selected-observation contradiction signals can describe the
  // same evidence. Use the bounded larger count instead of summing them.
  const count = Math.max(aggregateCount, observationCount);
  return { present: count > 0, count, context: count > 0 ? "same_theme" : "unknown" };
}

function findingFor(
  aggregate: CoachVeeraBehavioralReadModel,
  state: "historical_signal" | "emerging_finding" | "evidence_backed_finding",
) {
  const label = aggregate.primaryObservation.label;
  if (state === "historical_signal") {
    return {
      behaviorKey: aggregate.primaryObservation.behaviorKey,
      label,
      headline: `${label} is a working historical read.`,
      explanation: "Existing aggregate history suggests this pattern, while exact source mapping is still being strengthened.",
      whyNow: "Historical aggregate evidence is available.",
      question: `What do you notice about ${label} across recent conversations?`,
    };
  }
  return {
    behaviorKey: aggregate.primaryObservation.behaviorKey,
    label,
    headline: `${label} is showing up over time.`,
    explanation: state === "evidence_backed_finding"
      ? "Aggregate evidence is supported by independently meeting-bound authorized sources."
      : "Aggregate evidence is available, while exact source coverage is still developing.",
    whyNow: "The selected behavioral theme has current aggregate support.",
    question: `What do you notice about ${label} across recent conversations?`,
  };
}

function deepFreeze<T>(value: T): T {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    for (const nested of Object.values(value as Record<string, unknown>)) deepFreeze(nested);
    Object.freeze(value);
  }
  return value;
}

export function buildCoachLeaderLongitudinalRead(
  input: BuildCoachLeaderLongitudinalReadInput,
): CoachLeaderLongitudinalRead {
  if (input.status === "loading" || input.status === "revoked") return deepFreeze({ state: input.status });
  if (input.status === "unavailable") return deepFreeze({ state: "unavailable", retryable: input.retryable });

  const authorizedSourceReadiness = input.authorizedSources.status === "operational_error"
    ? "unavailable"
    : input.authorizedSources.status;
  const matches = input.aggregate.primaryObservation.source === "bi_v2"
    ? intersectAuthorizedAggregateSources(
      input.aggregateEvidenceRefs,
      input.authorizedSources.status === "operational_error" ? [] : input.authorizedSources.sources,
      input.aggregate.primaryObservation.behaviorKey,
    )
    : [];
  const coverage = buildCoverage(
    input.aggregate,
    matches,
    authorizedSourceReadiness,
    input.legacyEvidencePresent,
    input.observationWindowTruncated,
  );

  if (input.aggregate.primaryObservation.source === "no_behavioral_data") {
    return deepFreeze({ state: "insufficient_history", coverage });
  }

  const state = input.aggregate.primaryObservation.source === "compatibility_only_v1"
    ? "historical_signal"
    : !input.aggregate.sourceSummary.thinEvidence && independentMeetingCount(matches) >= 2
      ? "evidence_backed_finding"
      : "emerging_finding";
  const uniqueMeetingMatches = dedupeByMeeting(matches);
  const episodes = uniqueMeetingMatches.map(toEpisode);
  const supportingEpisodes = episodes.filter((episode) => episode.polarity === "support");
  const challengingEpisodes = episodes.filter((episode) => episode.polarity === "contextual_challenge");

  return deepFreeze({
    state,
    finding: findingFor(input.aggregate, state),
    confidence: input.aggregate.confidence,
    coverage,
    supportingEpisodes,
    challengingEpisodes,
    contradiction: contradictionFor(input.aggregate, matches),
  });
}
